/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card } from '@partiu/ui/components/atoms/Card.tsx';
import { AdaptiveStack } from '@partiu/ui/components/atoms/AdaptiveStack.tsx';
import { Button, HStack, Input, Link, ResponsiveValue, Spacer, Text, useDisclosure, useToast, VStack } from '@chakra-ui/react';
import { DashboardTemplate } from './DashboardTemplate.tsx';
import { FaFileExport, FaPencil, FaPlus } from 'react-icons/fa6';
import {
  DataGrid,
  GridColDef,
  GridRowParams,
  GridRowSelectionModel,
  GridRowsProp,
  GridSortModel,
} from '@mui/x-data-grid';
import { Box, createTheme, ThemeProvider } from '@mui/material';
import { useMutation, useQueryClient, UseQueryResult } from 'react-query';
import { PaginatedResponse } from '@partiu/api/types';
import React, { FunctionComponent, useCallback, useEffect, useState, useMemo } from 'react';
import { FaTrash } from 'react-icons/fa';
import { AddModelModal } from './AddModelModal.tsx';
import { DataGridProvider } from '@partiu/ui/contexts/DataGridProvider.tsx';
import { AddModalProps } from '../../types';
import { ExportConfirmationDialog } from '../organisms/ExportConfirmationDialog.tsx';
import { CSVColumn, downloadCSV } from '@partiu/ui/utils.ts';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@partiu/api/axios.ts';
import { RemoveModelModal } from './RemoveModelModal.tsx';
import { format } from 'date-fns';
import { ExportConfirmationDialog2 } from '../organisms/ExportConfirmationDialog2.tsx';
import { DashboardHooks } from '@partiu/api/dashboard/hooks.ts';

const MuiTheme = createTheme({
  palette: {
    mode: 'light',
  },
});

const DashTemplate = ({
  children,
  paddingBottom,
  modelName,
}: {
  children: React.ReactNode;
  paddingTop?: string;
  modelName?: string;
  paddingBottom?: string;
}) => {
  return (
    <DashboardTemplate title={modelName} paddingBottom={paddingBottom}>
      {children}
    </DashboardTemplate>
  );
};

export function DataTablePage<G, T>({
  searchPlaceholder,
  modelName,
  columns,
  listQueryKey,
  listHook,
  isRowSelectable,
  deleteMutationHook = () => useMutation(() => Promise.resolve()),
  updateMutationHook = () => useMutation(() => Promise.resolve()),
  addForm = null,
  addFormPath = '',
  addFormLabel,
  selectable = false,
  canExport = true,
  customButton,
  isEditable = false,
  canGenerateReport,
  removeForm = null,
  removePadding = false,
  modalSize,
  dataGridProps,
  extraFilters = {},
  filtersSlot,
}: IDataTablePageProps<G, T>) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const addModalDisclosure = useDisclosure();
  const removeModalDisclosure = useDisclosure();
  const exportDialogDisclosure = useDisclosure();
  const exportDialogDisclosure2 = useDisclosure();

  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | undefined>();
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [_search, _setSearch] = useState('');
  const [search, setSearch] = useState('');
  const [sortParam, setSort] = useState<string | undefined>(undefined);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [instance, setInstance] = useState<any | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Create delete mutation with proper options
  const deleteMutation = deleteMutationHook({
    onSuccess() {
      toast({
        title: 'Sucesso',
        description: `${modelName} removido com sucesso.`,
        status: 'success',
      });
      setRowSelectionModel([]);
      queryClient.invalidateQueries(listQueryKey).catch(console.error);
    },
    onError(error: any) {
      console.error('Delete error:', error);
      toast({
        title: 'Erro',
        description: `Não foi possível remover o ${modelName}. ${
          error?.response?.data?.message || 'Tente novamente mais tarde.'
        }`,
        status: 'error',
      });
    },
  });

  const { data = { results: [], count: 0 }, isLoading } = listHook({
    page: paginationModel.page + 1,
    page_size: isExporting ? 999999 : paginationModel.pageSize,
    search: search.replaceAll('.', '').replaceAll('-', '').replaceAll('/', ''),
    ordering: sortParam,
    ...extraFilters,
  });

  // Reseta para a primeira página quando os filtros extras mudam (mesmo padrão da busca)
  const extraFiltersKey = JSON.stringify(extraFilters);
  useEffect(() => {
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraFiltersKey]);

  // Memoize the data results to prevent unnecessary re-renders
  const memoizedResults = useMemo(() => data.results as GridRowsProp, [data.results]);

  const generateReport = async () => {
    try {
      await axiosInstance.post(`/report/`, {
        type: canGenerateReport,
        name: `Relatório de ${canGenerateReport === 'subscriptions-dashboard' ? 'subscrições' : 'usuários'} - ${format(new Date(), 'dd/MM/yyyy HH:mm')}`,
        ...extraFilters,
      });

      queryClient.invalidateQueries([DashboardHooks.Reports]).catch(console.error);

      const toastId = toast({
        title: 'Sucesso',
        description: (
          <VStack align="start" spacing={1}>
            <Text>Relatório gerado com sucesso. Quando estiver pronto você poderá baixar no menu relatórios.</Text>
            <Link
              fontWeight="bold"
              textDecoration="underline"
              onClick={() => {
                navigate('/relatorios');
                toast.close(toastId);
              }}
            >
              Clique aqui para ir em Relatórios
            </Link>
          </VStack>
        ),
        status: 'success',
        duration: 8000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Report generation error:', error);
      toast({
        title: 'Erro',
        description: `Não foi possível gerar o relatório. Tente novamente mais tarde.`,
        status: 'error',
      });
    }
  };

  const handleSortChange = useCallback((sortModels: GridSortModel) => {
    const sortModel = sortModels[0];
    if (!sortModel) {
      setSort(undefined);
      return;
    }
    const snakeCaseField = sortModel.field.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
    const sortAlt = sortModel.sort === 'desc' ? '-' : '';
    setSort(sortAlt + snakeCaseField);
  }, []);

  const handleRowSelectionChange = useCallback((newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection);
  }, []);

  const handleDeleteSelected = async () => {
    if (rowSelectionModel.length === 0) {
      toast({
        title: 'Aviso',
        description: 'Nenhum item selecionado para remoção.',
        status: 'warning',
      });
      return;
    }

    if (!deleteMutation) {
      toast({
        title: 'Aviso',
        description: 'Função de remoção não configurada.',
        status: 'warning',
      });
      return;
    }

    setIsDeleting(true);
    try {
      // Show loading toast
      toast({
        title: 'Removendo...',
        description: `Removendo ${rowSelectionModel.length} item(s)...`,
        status: 'info',
        duration: 2000,
      });

      // Delete items sequentially to avoid overwhelming the API
      for (const id of rowSelectionModel) {
        await deleteMutation.mutateAsync(Number(id));
        // Small delay between deletions
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Clear selection after all deletions
      setRowSelectionModel([]);
    } catch (error) {
      console.error('Batch delete error:', error);
      toast({
        title: 'Erro',
        description: 'Alguns itens não puderam ser removidos. Verifique os itens restantes.',
        status: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setSearch(_search);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500);

    setSearchTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [_search]);

  // Export effect
  useEffect(() => {
    if (isExporting && !isLoading && data.results.length > 0) {
      downloadCSV(
        modelName + '-' + new Date().toISOString().split('T')[0],
        columns
          .filter((c) => !c.renderCell)
          .map((c) => ({ ...c, valueFormatter: undefined })) as unknown as CSVColumn[],
        data.results
      );
      setIsExporting(false);
      exportDialogDisclosure.onClose();
    }
  }, [data, isLoading, isExporting, columns, modelName, exportDialogDisclosure]);

  // Memoize the selected instance for editing
  const selectedInstance = useMemo(() => {
    if (rowSelectionModel.length === 1) {
      return data.results.find((d: any) => d.id === rowSelectionModel[0]);
    }
    return null;
  }, [rowSelectionModel, data.results]);

  const renderContent = () => (
    <>
      {addForm && (
        <AddModelModal<T>
          modelName={modelName}
          onModelAdded={() => {
            queryClient.invalidateQueries(listQueryKey).catch(console.error);
            setRowSelectionModel([]);
          }}
          form={addForm}
          instance={instance}
          size={modalSize}
          {...addModalDisclosure}
          onClose={() => {
            setInstance(undefined);
            addModalDisclosure.onClose();
          }}
        />
      )}

      {removeForm && (
        <RemoveModelModal
          modelName={modelName}
          form={removeForm}
          size={modalSize}
          {...removeModalDisclosure}
          onClose={() => {
            setInstance(undefined);
            removeModalDisclosure.onClose();
            queryClient.invalidateQueries(listQueryKey).catch(console.error);
            setRowSelectionModel([]);
          }}
        />
      )}

      <ExportConfirmationDialog2 modelName={modelName} onConfirm={generateReport} {...exportDialogDisclosure2} />

      <ExportConfirmationDialog
        amount={data.count}
        modelName={modelName}
        onConfirm={() => {
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
          setIsExporting(true);
        }}
        {...exportDialogDisclosure}
      />

      <Box width={'100%'} p={removePadding ? 0 : 2}>
        <Card spacing={5} width={'100%'} padding={4}>
          <AdaptiveStack padding={0} align="center" width={'100%'}>
            <HStack width={'100%'}>
              {searchPlaceholder && (
                <Input
                  value={_search}
                  onChange={(e) => _setSearch(e.target.value)}
                  width={{ base: '100%', lg: '300px' }}
                  variant="filled"
                  placeholder={'Buscar por ' + searchPlaceholder}
                />
              )}
              <Text whiteSpace="nowrap" color={'#a5a5a5'} fontSize={'8pt'}>
                ({data.count} total)
              </Text>
              {selectable && rowSelectionModel.length > 0 && (
                <Text whiteSpace="nowrap" color={'blue.500'} fontSize={'8pt'} fontWeight="medium">
                  ({rowSelectionModel.length} selecionado(s))
                </Text>
              )}
            </HStack>
            <Spacer />
            <HStack justify={{ base: 'flex-start', lg: 'flex-end' }} width={'100%'} spacing={2}>
              {canGenerateReport && (
                <Button
                  onClick={exportDialogDisclosure2.onOpen}
                  colorScheme="blue"
                  variant={'outline'}
                  leftIcon={<FaFileExport />}
                  size="sm"
                >
                  Gerar Relatório
                </Button>
              )}

              {removeForm && (
                <Button
                  onClick={removeModalDisclosure.onOpen}
                  colorScheme="red"
                  variant={'solid'}
                  leftIcon={<FaTrash />}
                  size="sm"
                >
                  Remover em lote
                </Button>
              )}

              {canExport && (
                <Button
                  onClick={exportDialogDisclosure.onOpen}
                  colorScheme="blue"
                  variant={'outline'}
                  leftIcon={<FaFileExport />}
                  size="sm"
                >
                  Exportar
                </Button>
              )}

              {rowSelectionModel.length === 1 && addForm && isEditable && (
                <Button
                  colorScheme="green"
                  onClick={() => {
                    setInstance(selectedInstance);
                    addModalDisclosure.onOpen();
                  }}
                  variant={'outline'}
                  leftIcon={<FaPencil />}
                  size="sm"
                >
                  Editar
                </Button>
              )}

              {(rowSelectionModel.length === 0 || !selectable) && addForm && (
                <Button
                  colorScheme="green"
                  onClick={addModalDisclosure.onOpen}
                  variant={'outline'}
                  leftIcon={<FaPlus />}
                  size="sm"
                >
                  {addFormLabel || `Adicionar ${modelName}`}
                </Button>
              )}

              {(rowSelectionModel.length === 0 || !selectable) && addFormPath && (
                <Button
                  colorScheme="green"
                  onClick={() => navigate(addFormPath)}
                  variant={'outline'}
                  leftIcon={<FaPlus />}
                  size="sm"
                >
                  {addFormLabel || `Adicionar ${modelName}`}
                </Button>
              )}

              {rowSelectionModel.length > 0 && selectable && (
                <Button
                  onClick={handleDeleteSelected}
                  colorScheme="red"
                  variant={'solid'}
                  leftIcon={<FaTrash />}
                  size="sm"
                  isDisabled={!deleteMutation}
                  isLoading={isDeleting}
                >
                  Remover ({rowSelectionModel.length})
                </Button>
              )}

              {typeof customButton === 'function' &&
                React.createElement(customButton, {
                  onComplete: () => {
                    queryClient.invalidateQueries(listQueryKey).catch(console.error);
                  },
                })}
            </HStack>
          </AdaptiveStack>

          {filtersSlot}

          <DataGridProvider mutationHook={updateMutationHook} listQueryKey={listQueryKey || ''}>
            <ThemeProvider theme={MuiTheme}>
              <Box width={'100%'} height="70vh">
                {!isExporting && (
                  <DataGrid
                    checkboxSelection={selectable}
                    loading={isLoading}
                    rows={memoizedResults}
                    columns={columns}
                    paginationMode={'server'}
                    sortingMode={'server'}
                    rowCount={data.count}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    onSortModelChange={handleSortChange}
                    onRowSelectionModelChange={handleRowSelectionChange}
                    rowSelectionModel={rowSelectionModel}
                    isRowSelectable={isRowSelectable}
                    rowHeight={42}
                    disableRowSelectionOnClick={false}
                    keepNonExistentRowsSelected={false}
                    {...dataGridProps}
                  />
                )}
              </Box>
            </ThemeProvider>
          </DataGridProvider>
        </Card>
      </Box>
    </>
  );

  if (removePadding) {
    return renderContent();
  }

  return (
    <DashTemplate modelName={modelName} paddingBottom={'50px'}>
      {renderContent()}
    </DashTemplate>
  );
}

export interface IDataTablePageProps<G, T> {
  searchPlaceholder?: string;
  customButton?: FunctionComponent<{ onComplete: () => void }>;
  modelName: string;
  columns: GridColDef[];
  listQueryKey?: string;
  listHook: (params: Record<string, any>) => UseQueryResult<PaginatedResponse<G>>;
  updateMutationHook?: any;
  deleteMutationHook?: any;
  addForm?: FunctionComponent<AddModalProps<T>> | null;
  addFormPath?: string;
  selectable?: boolean;
  isRowSelectable?: (props: GridRowParams) => boolean;
  canExport?: boolean;
  isEditable?: boolean;
  modalSize?: ResponsiveValue<string>;
  canGenerateReport?: string;
  removeForm?: FunctionComponent<AddModalProps<T>> | null;
  addFormLabel?: string;
  removePadding?: boolean;
  dataGridProps?: Record<string, unknown>;
  /** Parâmetros extras enviados na listagem (query string) e no corpo do relatório (POST /report/). */
  extraFilters?: Record<string, unknown>;
  /** Área de filtros customizada, renderizada acima da grid. */
  filtersSlot?: React.ReactNode;
}
